'use client'

import { useState, useRef, useEffect } from 'react'
import { BookOpen, Save, Copy, AlertCircle } from 'lucide-react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Alert, AlertDescription, AlertTitle } from '@creanizer/ui'
import { Progress } from '@creanizer/ui'
import { useGenerator } from '../../../../hooks/useGenerator'
import { useStreamGenerator } from '../../../../hooks/useStreamGenerator'
import { useSettingsStore } from '../../../../stores/settingsStore'
import { useSceneStore } from '../../../../stores/sceneStore'
import { useWorldBuilderStore } from '../../../../stores/worldbuilderStore'
import { useCommunityStore } from '../../../../stores/communityStore'
import { useCharacterStore } from '../../../../stores/characterStore'
import { useItemStore } from '../../../../stores/itemStore'
import { useEnemyStore } from '../../../../stores/enemyStore'
import { useRelationshipStore } from '../../../../stores/relationshipStore'
import { useDungeonStore } from '../../../../stores/dungeonStore'
import { useSocietyStore } from '../../../../stores/societyStore'
import type { SceneInput, SceneResult, CharacterContext, ItemContext, EnemyContext, RelationshipContext, CommunityContext, SocietyContext } from '@creanizer/types'
import { SceneForm } from './SceneForm'
import { SceneFormSettings } from './SceneFormSettings'
import { SceneContextSelector } from './SceneContextSelector'
import { SceneResult as SceneResultComponent } from './SceneResult'
import { SceneSaved } from './SceneSaved'

export function SceneGenerator() {
  const { isApiKeySet } = useSettingsStore()
  const { 
    scenes, 
    addScene, 
    addBatchScenes, 
    removeScene, 
    exportAsJSON, 
    importFromJSON,
    createSceneChain,
    addToChain,
    currentScene,
    chapterSections,
    updateSectionStatus,
    getNextPendingSection,
    sceneChains
  } = useSceneStore()
  const { savedWorlds, currentWorld } = useWorldBuilderStore()
  const { savedCommunities } = useCommunityStore()
  const { characters } = useCharacterStore()
  const { items } = useItemStore()
  const { enemies } = useEnemyStore()
  const { savedRelationships } = useRelationshipStore()
  const { dungeons } = useDungeonStore()
  const { savedSocieties } = useSocietyStore()
  const generator = useGenerator('scene')
  const streamGenerator = useStreamGenerator('scene')
  
  // UI状態
  const [activeTab, setActiveTab] = useState('generate')
  const [selectedWorldId, setSelectedWorldId] = useState<string>('')
  const [selectedCommunityIds, setSelectedCommunityIds] = useState<string[]>([])
  const [selectedSocietyIds, setSelectedSocietyIds] = useState<string[]>([])
  const [selectedDungeonId, setSelectedDungeonId] = useState<string>('')
  const [selectedCharacterIds, setSelectedCharacterIds] = useState<string[]>([])
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([])
  const [selectedEnemyIds, setSelectedEnemyIds] = useState<string[]>([])
  const [selectedRelationshipIds, setSelectedRelationshipIds] = useState<string[]>([])
  const [useStreaming, setUseStreaming] = useState<boolean>(true)
  const [currentChainId, setCurrentChainId] = useState<string>('')
  
  // 入力状態
  const [input, setInput] = useState<SceneInput>({
    mode: 'standard',
    sceneType: 'description',
    perspective: 'third_limited',
    writingStyle: 'novel',
    targetLength: 1500,
    creativityLevel: 7
  })
  
  // 連続生成モードのサブタイプを追跡
  const [continuationSourceType, setContinuationSourceType] = useState<'saved' | 'chapter'>('saved')
  
  // 結果状態
  const [result, setResult] = useState<SceneResult | null>(null)
  const [error, setError] = useState<string>('')
  const resultRef = useRef<HTMLDivElement>(null)
  
  // 通知状態
  const [notification, setNotification] = useState<string>('')

  // デバッグ用のログ出力
  useEffect(() => {
    console.log('SceneGenerator - データ状況:')
    console.log('  キャラクター数:', characters.length)
    console.log('  アイテム数:', items.length)
    console.log('  エネミー数:', enemies.length)
    console.log('  世界観数:', savedWorlds.length)
    console.log('  コミュニティ数:', savedCommunities.length)
    console.log('  ソサエティ数:', savedSocieties.length)
    console.log('  ダンジョン数:', dungeons.length)
    console.log('  関係性数:', savedRelationships.length)
  }, [characters, items, enemies, savedWorlds, savedCommunities, savedSocieties, dungeons, savedRelationships])

  // inputステートの変更を監視（特にuploadedContent）
  useEffect(() => {
    console.log('【デバッグ】inputステート更新検知:', {
      mode: input.mode,
      uploadedContent: input.uploadedContent ? '✅ あり' : '❌ なし',
      uploadedContentLength: input.uploadedContent?.length || 0,
      uploadedContentPreview: input.uploadedContent?.substring(0, 100) || '',
      uploadedContentType: typeof input.uploadedContent
    })
  }, [input.uploadedContent])

  // 世界観コンテキストの生成
  const generateWorldContextText = (worldId: string): string => {
    const world = savedWorlds.find(w => w.id === worldId)
    if (!world) return ''
    
    // 世界観の全データをJSON形式で渡す
    return JSON.stringify(world.result)
  }

  // ダンジョンコンテキストの生成
  const generateDungeonContextText = (dungeonId: string): string => {
    const dungeon = dungeons.find(d => d.id === dungeonId)
    if (!dungeon) return ''
    
    // ダンジョンの全データをJSON形式で渡す
    return JSON.stringify(dungeon.result)
  }

  // キャラクターコンテキストの生成
  const generateCharacterContexts = (): CharacterContext[] => {
    console.log('【デバッグ】generateCharacterContexts開始:', {
      selectedCharacterIds: selectedCharacterIds,
      charactersLength: characters.length
    })
    
    const contexts = selectedCharacterIds.map(id => {
      const character = characters.find(c => c.id === id)
      console.log('【デバッグ】キャラクター検索:', {
        検索ID: id,
        見つかった: !!character,
        キャラクター名: character?.result?.name
      })
      if (!character) return null
      
      // 詳細なデバッグ情報を出力
      const characterData = character.result
      console.log(`【デバッグ】キャラクター詳細 (${characterData.name}):`, {
        基本情報: {
          名前: characterData.name,
          年齢: characterData.age,
          性別: characterData.gender,
          役割: characterData.role
        },
        背景: {
          家族構成: characterData.background?.family,
          人間関係: characterData.background?.relationships,
          出身地: characterData.background?.birthplace,
          職業: characterData.background?.occupation
        },
        性格: {
          特性: characterData.personality?.traits,
          価値観: characterData.personality?.values
        },
        完全なbackgroundオブジェクト: characterData.background
      })
      
      return {
        id,
        name: character.result.name,
        role: 'supporting' as const,
        data: JSON.stringify(character.result)
      }
    }).filter(Boolean) as CharacterContext[]
    
    console.log('【デバッグ】生成されたキャラクターコンテキスト:', {
      contextsLength: contexts.length,
      contexts: contexts.map(c => ({ id: c.id, name: c.name }))
    })
    
    // グローバル変数に保存（デバッグ用）
    ;(window as any).__debugCharacterContexts = contexts
    ;(window as any).__debugCharacterData = contexts.map(c => JSON.parse(c.data))
    console.log('【デバッグ】コンソールから以下でアクセス可能:')
    console.log('  window.__debugCharacterContexts - コンテキストの配列')
    console.log('  window.__debugCharacterData - パースされたキャラクターデータの配列')
    console.log('  例: window.__debugCharacterData[0].background.family')
    
    return contexts
  }

  // アイテムコンテキストの生成
  const generateItemContexts = (): ItemContext[] => {
    return selectedItemIds.map(id => {
      const item = items.find(i => i.id === id)
      if (!item) return null
      
      return {
        id,
        name: item.result.basicInfo.name,
        usage: 'mentioned' as const,
        data: JSON.stringify(item.result)
      }
    }).filter(Boolean) as ItemContext[]
  }

  // エネミーコンテキストの生成
  const generateEnemyContexts = (): EnemyContext[] => {
    return selectedEnemyIds.map(id => {
      const enemy = enemies.find(e => e.id === id)
      if (!enemy) return null
      
      return {
        id,
        name: enemy.result.basicInfo.name,
        role: 'threat' as const,
        data: JSON.stringify(enemy.result)
      }
    }).filter(Boolean) as EnemyContext[]
  }

  // 関係性コンテキストの生成
  const generateRelationshipContexts = (): RelationshipContext[] => {
    console.log('【デバッグ】generateRelationshipContexts開始:', {
      selectedRelationshipIds: selectedRelationshipIds,
      relationshipsLength: savedRelationships.length
    })
    
    const contexts = selectedRelationshipIds.map(id => {
      const relationship = savedRelationships.find(r => r.id === id)
      console.log('【デバッグ】関係性検索:', {
        検索ID: id,
        見つかった: !!relationship,
        タイトル: relationship?.title
      })
      if (!relationship) return null
      
      const firstChar = relationship.result.characters[0]
      const secondChar = relationship.result.characters[1]
      const firstRelation = relationship.result.relationships[0]
      return {
        id,
        character1: firstChar?.name || '',
        character2: secondChar?.name || '',
        relationshipType: firstRelation?.relationshipType || firstRelation?.sourceToTargetType || '',
        data: JSON.stringify(relationship.result)
      }
    }).filter(Boolean) as RelationshipContext[]
    
    console.log('【デバッグ】生成された関係性コンテキスト:', {
      contextsLength: contexts.length,
      contexts: contexts.map(c => ({ 
        id: c.id, 
        character1: c.character1,
        character2: c.character2 
      }))
    })
    
    return contexts
  }

  // コミュニティコンテキストの生成（複数対応）
  const generateCommunityContexts = (): CommunityContext[] => {
    return selectedCommunityIds.map(id => {
      const community = savedCommunities.find(c => c.id === id)
      if (!community) return null
      
      return {
        id,
        name: community.result.basicInfo.name,
        type: community.result.basicInfo.type,
        data: JSON.stringify(community.result)
      }
    }).filter(Boolean) as CommunityContext[]
  }

  // ソサエティコンテキストの生成（複数対応）
  const generateSocietyContexts = (): SocietyContext[] => {
    return selectedSocietyIds.map(id => {
      const society = savedSocieties.find(s => s.id === id)
      if (!society) return null
      
      return {
        id,
        name: society.result.basicInfo.name,
        type: society.result.basicInfo.type,
        data: JSON.stringify(society.result)
      }
    }).filter(Boolean) as SocietyContext[]
  }

  // 生成処理（ストリーミング対応）
  const handleGenerateWithStreaming = async () => {
    console.log('【デバッグ】handleGenerateWithStreaming開始:', {
      mode: input.mode,
      currentChainId: currentChainId,
      currentChainIdタイプ: typeof currentChainId,
      currentChainId長さ: currentChainId?.length,
      sceneChainsCount: sceneChains.length,
      isApiKeySet: isApiKeySet,
      selectedCharacterIds: selectedCharacterIds,
      selectedRelationshipIds: selectedRelationshipIds
    })
    
    if (!isApiKeySet) {
      setError('APIキーが設定されていません')
      return
    }

    setError('')
    setNotification('')

    // デバッグ：キャラクター選択状態を確認
    console.log('【デバッグ】キャラクター選択状態:', {
      selectedCharacterIds: selectedCharacterIds,
      selectedCharacterIdsLength: selectedCharacterIds.length,
      selectedRelationshipIds: selectedRelationshipIds,
      selectedRelationshipIdsLength: selectedRelationshipIds.length
    })

    // コンテキスト生成
    const characterContexts = generateCharacterContexts()
    const relationshipContexts = generateRelationshipContexts()
    
    console.log('【デバッグ】生成されたコンテキスト:', {
      characterContexts: characterContexts,
      characterContextsLength: characterContexts.length,
      relationshipContexts: relationshipContexts,
      relationshipContextsLength: relationshipContexts.length
    })

    console.log('【デバッグ】finalInput作成前のinput:', {
      mode: input.mode,
      previousSceneId: input.previousSceneId,
      previousSceneText: input.previousSceneText,
      previousSceneTextLength: input.previousSceneText?.length || 0,
      previousSceneTextPreview: input.previousSceneText?.substring(0, 100) || 'なし',
      uploadedContent: input.uploadedContent ? '✅ あり' : '❌ なし',
      uploadedContentLength: input.uploadedContent?.length || 0,
      uploadedContentPreview: input.uploadedContent?.substring(0, 100) || '',
      currentChainId: currentChainId,
      containsチェーンコンテキスト: input.previousSceneText?.includes('【現在のシーン】') || false
    })
    
    // コンテキストの準備
    const finalInput: SceneInput = {
      ...input,
      worldContext: selectedWorldId ? generateWorldContextText(selectedWorldId) : undefined,
      communityContexts: generateCommunityContexts(),
      societyContexts: generateSocietyContexts(),
      dungeonContext: selectedDungeonId ? generateDungeonContextText(selectedDungeonId) : undefined,
      characterContexts: characterContexts,
      itemContexts: generateItemContexts(),
      enemyContexts: generateEnemyContexts(),
      relationshipContexts: relationshipContexts,
      // uploadedContentは既にinputに含まれているため、そのまま渡される
    }

    console.log('【デバッグ】生成時のinput:', {
      mode: finalInput.mode,
      chapterSection: finalInput.chapterSection,
      previousSceneTextLength: finalInput.previousSceneText?.length || 0,
      previousSceneTextLast100: finalInput.previousSceneText?.slice(-100) || '',
      plotContextLength: finalInput.plotContext?.length || 0,
      uploadedContentLength: finalInput.uploadedContent?.length || 0,
      uploadedContentPreview: finalInput.uploadedContent?.substring(0, 100) || '',
      uploadedContentStatus: finalInput.uploadedContent ? '✅ 参考資料あり' : '❌ 参考資料なし',
      characterContextsInFinal: finalInput.characterContexts,
      relationshipContextsInFinal: finalInput.relationshipContexts
    })

    try {
      if (useStreaming) {
        // ストリーミング生成
        await streamGenerator.generateStream(finalInput, {
          onChunk: (chunk) => {
            // チャンク受信時の処理（必要に応じて）
          },
          onProgress: (bytes) => {
            // 進捗更新（必要に応じて）
          },
          onComplete: (result) => {
            const resultData = result.result || result
            console.log('【デバッグ】ストリーミング生成結果:', {
              hasResult: !!resultData,
              hasFullText: !!resultData.fullText,
              fullTextLength: resultData.fullText?.length || 0,
              fullTextPreview: resultData.fullText?.substring(0, 100) || 'なし'
            })
            setResult(resultData as SceneResult)
            if (resultRef.current) {
              resultRef.current.scrollIntoView({ behavior: 'smooth' })
            }
          },
          onError: (error) => {
            setError(error.message)
          }
        })
      } else {
        // 通常の生成
        console.log('【デバッグ】通常生成実行:', {
          mode: finalInput.mode,
          characterContextsCount: finalInput.characterContexts?.length || 0,
          characterNames: finalInput.characterContexts?.map(c => c.name) || [],
          sceneSummary: finalInput.sceneSummary || '（概要なし）',
          perspective: finalInput.perspective,
          currentChainId: currentChainId
        })
        const response = await generator.mutateAsync(finalInput)
        const resultData = response.result || response
        console.log('【デバッグ】通常生成結果:', {
          hasResult: !!resultData,
          hasFullText: !!(resultData as any).fullText,
          fullTextLength: (resultData as any).fullText?.length || 0,
          fullTextPreview: (resultData as any).fullText?.substring(0, 100) || 'なし'
        })
        setResult(resultData as SceneResult)
        if (resultRef.current) {
          resultRef.current.scrollIntoView({ behavior: 'smooth' })
        }
      }
    } catch (err: any) {
      setError(err.message || '生成中にエラーが発生しました')
    }
  }

  // 保存処理
  const handleSave = (sceneResult: SceneResult) => {
    console.log('【デバッグ】handleSave開始:', {
      mode: input.mode,
      chapterSection: input.chapterSection,
      currentSceneBefore: currentScene?.id,
      currentChainId,
      resultHasFullText: !!sceneResult.fullText,
      fullTextLength: sceneResult.fullText?.length || 0,
      fullTextPreview: sceneResult.fullText?.substring(0, 100) || 'なし'
    })
    
    // addSceneは新しいシーンを返す
    const newScene = addScene(sceneResult, input)
    
    console.log('【デバッグ】addScene後:', {
      新しいシーンID: newScene.id,
      新しいシーンタイトル: newScene.result.title,
      currentSceneAfter: currentScene?.id,
      最新3件のシーンID: scenes.slice(0, 3).map(s => ({ id: s.id, title: s.result?.title }))
    })
    
    // 連続生成モード＆保存済みシーン継続の場合のみチェーンを管理
    console.log('【デバッグ】チェーン管理チェック:', {
      mode: input.mode,
      continuationSourceType: continuationSourceType,
      currentChainId: currentChainId,
      shouldManageChain: input.mode === 'continuation' && continuationSourceType === 'saved'
    })
    
    if (input.mode === 'continuation' && continuationSourceType === 'saved') {
      if (!currentChainId) {
        // 初回保存時：新しいチェーンを作成
        const chainName = `シーンチェーン_${new Date().toLocaleDateString('ja-JP')}`
        const newChainId = createSceneChain(chainName, newScene.id)
        setCurrentChainId(newChainId)
        console.log('【デバッグ】新しいチェーン作成:', { 
          newChainId, 
          chainName,
          firstSceneId: newScene.id,
          currentChainIdBefore: currentChainId
        })
      } else {
        // 2回目以降：既存のチェーンに追加（設定も保存）
        const settings = {
          // 基本設定
          continuationType: input.continuationType,
          genre: input.genre,
          customGenre: input.customGenre,
          perspective: input.perspective,
          writingStyle: input.writingStyle,
          customStyle: input.customStyle,
          savedCustomStyleId: input.savedCustomStyleId,
          
          // シーン設定
          sceneType: input.sceneType,
          customType: input.customType,
          atmosphere: input.atmosphere,
          customAtmosphere: input.customAtmosphere,
          
          // 生成設定
          targetLength: input.targetLength,
          lengthType: input.lengthType,
          creativityLevel: input.creativityLevel,
          variationMode: input.variationMode,
          useProMode: input.useProMode,
          useProLiteMode: input.useProLiteMode,
          
          // 内容設定
          focusElements: input.focusElements,
          avoidElements: input.avoidElements,
          emotionalTone: input.emotionalTone,
          themes: input.themes,
          
          // その他
          additionalSettings: input.additionalSettings
          // uploadedContentは保存しない（セッション固有の参考資料のため）
        }
        addToChain(currentChainId, newScene.id, settings)
        console.log('【デバッグ】チェーンに追加:', { currentChainId, sceneId: newScene.id, settings })
      }
    }
    
    // 長編執筆モードの場合、セクションステータスを更新
    if (input.mode === 'continuation' && input.chapterSection) {
      const { chapterId, sectionNumber } = input.chapterSection
      console.log('【デバッグ】セクション更新:', {
        chapterId,
        sectionNumber,
        使用するsceneId: newScene.id
      })
      
      if (chapterId && sectionNumber) {
        // セクションを完了状態に更新（新しいシーンのIDを使用）
        updateSectionStatus(chapterId, sectionNumber, 'completed', newScene.id)
        setNotification(`セクション${sectionNumber}が完了しました`)
        
        // 次のセクションがある場合、自動的にin_progressに設定
        const nextSection = getNextPendingSection(chapterId)
        if (nextSection) {
          updateSectionStatus(chapterId, nextSection.sectionNumber, 'in_progress')
        }
        
        // 章のセクション状態を確認
        const chapter = chapterSections.find(ch => ch.id === chapterId)
        if (chapter) {
          console.log('【デバッグ】章の現在のセクション状態:', 
            chapter.sections.map(s => ({
              sectionNumber: s.sectionNumber,
              status: s.status,
              sceneId: s.sceneId
            }))
          )
        }
      }
    } else {
      setNotification('シーンを保存しました')
    }
    
    setTimeout(() => setNotification(''), 3000)
  }

  // 保存済みかチェック
  const isSaved = (sceneResult: SceneResult): boolean => {
    if (!sceneResult || !sceneResult.title || !sceneResult.fullText) return false
    return scenes.some(s => 
      s?.result?.title === sceneResult.title && 
      s?.result?.fullText === sceneResult.fullText
    )
  }

  // エクスポート処理
  const handleExport = () => {
    exportAsJSON()
  }

  // インポート処理
  const handleImport = () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = '.json'
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0]
      if (file) {
        const reader = new FileReader()
        reader.onload = (e) => {
          const content = e.target?.result as string
          importFromJSON(content)
        }
        reader.readAsText(file)
      }
    }
    input.click()
  }

  return (
    <div className="space-y-6">
      <div className="card">
        <div className="flex items-center gap-2 mb-2">
          <BookOpen className="h-5 w-5" />
          <h2 className="text-xl font-semibold">シーン描写ジェネレータ</h2>
        </div>
        <p className="text-muted-foreground mb-4">
          小説風のシーン描写を生成し、物語を紡ぎます
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="generate">生成</TabsTrigger>
          <TabsTrigger value="saved">
            保存済み ({scenes.length}/500)
          </TabsTrigger>
        </TabsList>

        <TabsContent value="generate" className="space-y-6">
          <div className="card">
            <h3 className="text-lg font-semibold mb-4">生成設定</h3>
            
            {/* 世界観・要素選択 */}
            {(savedWorlds.length > 0 || savedCommunities.length > 0 || savedSocieties.length > 0 || dungeons.length > 0 || characters.length > 0 || items.length > 0) && (
              <SceneContextSelector
                savedWorlds={savedWorlds}
                communities={savedCommunities}
                societies={savedSocieties}
                dungeons={dungeons}
                characters={characters}
                items={items}
                enemies={enemies}
                relationships={savedRelationships}
                selectedWorldId={selectedWorldId}
                selectedCommunityIds={selectedCommunityIds}
                selectedSocietyIds={selectedSocietyIds}
                selectedDungeonId={selectedDungeonId}
                selectedCharacterIds={selectedCharacterIds}
                selectedItemIds={selectedItemIds}
                selectedEnemyIds={selectedEnemyIds}
                selectedRelationshipIds={selectedRelationshipIds}
                onWorldChange={setSelectedWorldId}
                onCommunitiesChange={setSelectedCommunityIds}
                onSocietiesChange={setSelectedSocietyIds}
                onDungeonChange={setSelectedDungeonId}
                onCharactersChange={setSelectedCharacterIds}
                onItemsChange={setSelectedItemIds}
                onEnemiesChange={setSelectedEnemyIds}
                onRelationshipsChange={setSelectedRelationshipIds}
              />
            )}
            
            {/* モード別フォーム */}
            <SceneForm
              input={input}
              onChange={(newInput) => {
                console.log('【デバッグ】index.tsx setInput呼び出し:', {
                  mode: newInput.mode,
                  previousSceneId: newInput.previousSceneId,
                  previousSceneTextLength: newInput.previousSceneText?.length || 0,
                  previousSceneTextPreview: newInput.previousSceneText?.substring(0, 100) || 'なし',
                  uploadedContent: newInput.uploadedContent ? '✅ あり' : '❌ なし',
                  uploadedContentLength: newInput.uploadedContent?.length || 0,
                  uploadedContentPreview: newInput.uploadedContent?.substring(0, 100) || ''
                })
                setInput(newInput)
              }}
              scenes={scenes}
              currentChainId={currentChainId}
              continuationSourceType={continuationSourceType}
              onContinuationSourceTypeChange={setContinuationSourceType}
              onChainCreate={(name, firstSceneId) => {
                const newChainId = createSceneChain(name, firstSceneId)
                setCurrentChainId(newChainId)
                console.log('【デバッグ】チェーン作成:', { 
                  newChainId, 
                  name, 
                  firstSceneId,
                  currentChainIdAfterSet: newChainId
                })
              }}
              onChainSelect={(chainId) => {
                setCurrentChainId(chainId)
                console.log('【デバッグ】チェーン選択:', { 
                  chainId,
                  previousChainId: currentChainId,
                  newChainId: chainId
                })
              }}
              onChainReset={() => {
                console.log('【デバッグ】チェーンリセット前:', { currentChainId })
                setCurrentChainId('')
                console.log('【デバッグ】チェーンリセット後: 空文字列に設定')
              }}
              sceneChains={sceneChains}
            />
            
            {/* 詳細設定 */}
            <SceneFormSettings
              input={input}
              onChange={setInput}
              useStreaming={useStreaming}
              onStreamingChange={setUseStreaming}
            />
            
            <div className="flex justify-end mt-4">
              <Button
                onClick={handleGenerateWithStreaming}
                disabled={generator.isPending || streamGenerator.isStreaming}
              >
                {generator.isPending || streamGenerator.isStreaming ? '生成中...' : 'シーンを生成'}
              </Button>
            </div>
          </div>

          {/* エラー表示 */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>エラー</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* ストリーミング表示 */}
          {streamGenerator.isStreaming && (
            <div className="card">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>生成中...</span>
                  <span>{Math.round(streamGenerator.progress)}%</span>
                </div>
                <Progress value={streamGenerator.progress} />
                {streamGenerator.partialContent && (
                  <div className="mt-4 p-4 bg-muted rounded max-h-48 overflow-y-auto">
                    <pre className="text-xs whitespace-pre-wrap">
                      {streamGenerator.partialContent.slice(-500)}
                      {streamGenerator.partialContent.length > 500 && '...'}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 通知表示 */}
          {notification && (
            <Alert>
              <AlertDescription>{notification}</AlertDescription>
            </Alert>
          )}
        </TabsContent>
        
        <TabsContent value="saved">
          <SceneSaved
            scenes={scenes}
            onExport={handleExport}
            onImport={handleImport}
            onRemove={removeScene}
            onSelect={(scene) => {
              setResult(scene.result)
              setInput(scene.input)
              setActiveTab('generate')
            }}
          />
        </TabsContent>
      </Tabs>
      
      {/* 結果表示 */}
      {result && (
        <div className="card" ref={resultRef}>
          <div className="flex flex-row items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">生成結果</h3>
            <div className="flex gap-2">
              {!isSaved(result) && (
                <Button onClick={() => handleSave(result)}>
                  <Save className="h-4 w-4 mr-2" />
                  保存
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => navigator.clipboard.writeText(result.fullText)}
              >
                <Copy className="h-4 w-4 mr-2" />
                コピー
              </Button>
            </div>
          </div>
          <SceneResultComponent
            result={result}
            onSave={handleSave}
            isSaved={isSaved(result)}
          />
        </div>
      )}
    </div>
  )
}